import { useMemo, useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';

import {
  useSponsorshipPriorities,
  useSponsorshipStatuses
} from 'hooks/useBeneficiaries';
import { useUserRoles } from 'hooks/useUsers';

import { RHFSelect, RHFControl, RHFDatePicker } from 'components/common/form';
import { Card, Col, Row, Container, Alert } from 'react-bootstrap';
import AuthContext from 'context/authContext';

export const SponsorshipStatus = ({ sectionRef, sideNavLink }) => {
  const { sponsorshipPriorities } = useSponsorshipPriorities();
  const { sponsorshipStatuses } = useSponsorshipStatuses();
  const { userRoles } = useUserRoles();
  const { user } = useContext(AuthContext);

  const sponsorshipStatusOptions = useMemo(() => {
    if (sponsorshipStatuses) {
      return [...sponsorshipStatuses]
        .sort((a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100))
        .map(status => ({
          label: status.is_hidden
            ? `${status.status} (left programme)`
            : status.is_auto
            ? `${status.status} (automatic)`
            : status.status,
          value: status.id
        }));
    }
    return [];
  }, [sponsorshipStatuses]);

  // Warn before a child is moved to a "left the programme" status: they drop
  // out of the lists and an alert goes to the main branch to reassign the
  // sponsor. Automatic statuses are recalculated from sponsorship coverage.
  const { watch } = useFormContext();
  const selectedStatusId = watch('sponsorship.statusId');
  const selectedStatus = useMemo(
    () => sponsorshipStatuses?.find(st => st.id === selectedStatusId),
    [sponsorshipStatuses, selectedStatusId]
  );

  const sponsorshipPriorityOptions = useMemo(() => {
    if (sponsorshipPriorities) {
      return sponsorshipPriorities.map(priority => ({
        label: priority.priority_type,
        value: priority.id
      }));
    }
    return [];
  }, [sponsorshipPriorities]);

  const staffOptions = useMemo(() => {
    if (!userRoles) return [];
    return userRoles.map(userRole => ({
      label: `${userRole.user.first_name} ${userRole.user.last_name}`,
      value: userRole.user.id
    }));
  }, [userRoles]);

  const createdByName = user ? `${user.first_name} ${user.last_name}` : '';

  return (
    <Card className="border shadow-none mb-3" ref={sectionRef} id={sideNavLink}>
      <Card.Header className="pb-0">
        <div className="dFlexRowStartSpace pt-3">
          <Card.Title as="h5">Sponsorship Status</Card.Title>
        </div>
      </Card.Header>
      <Card.Body>
        <Container className="p-0">
          <Row className="gx-3">
            <Col xs={12} sm={6} className="pt-3">
              <RHFSelect
                name="sponsorship.statusId"
                label="Status"
                options={sponsorshipStatusOptions}
                required
              />
              {selectedStatus?.is_hidden && (
                <>
                  <Alert variant="warning" className="mt-2 py-2 fs--1 mb-0">
                    <strong>{selectedStatus.status}</strong> means this child
                    has left the programme. They will be hidden from beneficiary
                    lists (visible only via the status filter and reports), any
                    active sponsorship is ended so the donation can be
                    reassigned, and an alert is raised naming the sponsor.
                  </Alert>
                  <div className="mt-2">
                    <RHFDatePicker
                      name="sponsorship.leftProgrammeAt"
                      label="Date left programme"
                      noOptional={true}
                    />
                    <p className="text-600 fs--1 mb-0">
                      Leave blank to use today&apos;s date.
                    </p>
                  </div>
                </>
              )}
              {selectedStatus?.is_auto && (
                <p className="text-600 fs--1 mt-2 mb-0">
                  This status is set automatically from sponsorship coverage and
                  will update itself when sponsorships change.
                </p>
              )}
            </Col>
            <Col xs={12} sm={6} className="pt-3">
              <RHFSelect
                name="sponsorship.priorityId"
                label="Priority"
                options={sponsorshipPriorityOptions}
                required
              />
            </Col>
          </Row>
          <Row className="gx-3 mt-2">
            <Col xs={12} sm={6}>
              <RHFSelect
                name="assignedStaffId"
                label="Assigned Staff / Officer"
                options={staffOptions}
                placeholder="Select staff member..."
              />
            </Col>
            <Col xs={12} sm={6}>
              <RHFControl
                name="createdBy"
                label="Created By"
                value={createdByName}
                disabled
                helpText="Auto-filled"
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={12}>
              <RHFControl
                name="caseNotes"
                label="Case Notes / Remarks"
                as="textarea"
                rows={4}
                placeholder="Describe the beneficiary's situation, special circumstances, or any important notes for staff..."
              />
            </Col>
          </Row>
        </Container>
      </Card.Body>
    </Card>
  );
};

SponsorshipStatus.propTypes = {
  sectionRef: PropTypes.object,
  sideNavLink: PropTypes.string
};
